import React, { useState, useCallback } from 'react';
import Image from 'next/image';
import { useComponentVisibility } from '../../../../../hooks/useComponentVisibility';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';

export interface LazyImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
  style?: React.CSSProperties;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  placeholder?: 'blur' | 'empty';
  priority?: boolean;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  width,
  height,
  fill = false,
  className,
  style,
  objectFit = 'contain',
  objectPosition,
  placeholder = 'empty',
  priority = false,
}) => {
  const [shouldLoad, setShouldLoad] = useState(priority);

  const handleVisible = useCallback(() => {
    setShouldLoad(true);
  }, []);

  const ref = useComponentVisibility(handleVisible, {
    threshold: 0.1,
    rootMargin: '50px',
  });

  const shouldLoadImage = priority || shouldLoad;

  return (
    <div ref={ref} className={className} style={style}>
      {shouldLoadImage ? (
        fill ? (
          <Image
            key={`lazy-fill-${src}`}
            src={src}
            alt={alt}
            fill
            loading={priority ? 'eager' : 'lazy'}
            style={{ objectFit, objectPosition }}
            placeholder={placeholder}
          />
        ) : (
          <Image
            key={`lazy-sized-${src}-${width}x${height}`}
            src={src}
            alt={alt}
            width={width}
            height={height}
            loading={priority ? 'eager' : 'lazy'}
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            placeholder={placeholder}
          />
        )
      ) : (
        <LoadingSkeleton height={fill ? 400 : height || 200} />
      )}
    </div>
  );
};
